import { Bot, Send, Sparkles, Trash2, User } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { ServiceBanner } from '@/components/ServiceBanner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useBanner } from '@/hooks/useBanner'
import { trackEvent } from '@/lib/analytics'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

const TITLES = new Set(['mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'rev'])
function getFirstName(name?: string): string {
  if (!name) return ''
  const parts = name.trim().split(/\s+/)
  const first = parts.find((p) => !TITLES.has(p.toLowerCase().replace(/\.$/, '')))
  return first ?? ''
}

function getGreeting(name?: string): string {
  const firstName = getFirstName(name)
  const hour = new Date().getHours()
  let salutation: string
  if (hour >= 5 && hour < 12) salutation = 'Good morning'
  else if (hour >= 12 && hour < 18) salutation = 'Good afternoon'
  else if (hour >= 18 && hour < 22) salutation = 'Good evening'
  else salutation = 'Hi'
  return firstName ? `${salutation}, ${firstName}` : salutation
}

const AZURE_FUNCTION_URL =
  'https://func-reinz-data-dev-03.azurewebsites.net/api/agent-router-answer?code=-B8cmfbFsm-qXq7lRYTOXhp2Kqbae4rPvSmeWm1qCQxbAzFufpsctg=='

interface AskReinzScreenProps {
  messages: Message[]
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
  imisConversationId: string | null
  setImisConversationId: React.Dispatch<React.SetStateAction<string | null>>
  ragConversationId: string | null
  setRagConversationId: React.Dispatch<React.SetStateAction<string | null>>
  userName?: string
}

export function AskReinzScreen({
  messages,
  setMessages,
  imisConversationId,
  setImisConversationId,
  ragConversationId,
  setRagConversationId,
  userName,
}: AskReinzScreenProps) {
  const banner = useBanner('askreinz')
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    trackEvent('screen_viewed', { screen: 'ask_reinz' })
  }, [])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  const handleSend = async () => {
    if (!input.trim()) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    trackEvent('ask_reinz_question', { question_length: userMessage.content.length })
    setInput('')
    setIsTyping(true)

    try {
      const res = await fetch(AZURE_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: userMessage.content,
          imis_conversation_id: imisConversationId,
          rag_conversation_id: ragConversationId,
        }),
      })

      const raw = await res.text()
      let answer = raw
      try {
        const data = JSON.parse(raw)
        answer = data.body || data.answer || raw
        if (data.imis_conversation_id !== undefined)
          setImisConversationId(data.imis_conversation_id)
        if (data.rag_conversation_id !== undefined) setRagConversationId(data.rag_conversation_id)
      } catch {
        answer = raw
      }

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: answer,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, aiMessage])
    } catch (err) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "Sorry, I'm having trouble connecting. Please try again.",
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsTyping(false)
    }
  }

  const handleClearChat = () => {
    trackEvent('ask_reinz_chat_cleared', { message_count: messages.length })
    setMessages([])
    setImisConversationId(null)
    setRagConversationId(null)
  }

  return (
    <div className="flex flex-col h-full -mx-4 -my-4">
      {/* Chat Header */}
      <div className="bg-gradient-reinz px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-foreground/20 flex items-center justify-center shrink-0">
            <Bot className="w-5 h-5 text-primary-foreground" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-primary-foreground font-heading">AskREINZ</h2>
            <p className="text-xs text-primary-foreground/70">
              Knowledge, resources, and guidance at your fingertips, backed by REINZ and 10+ trusted
              industry sources
            </p>
          </div>
        </div>
      </div>

      <ServiceBanner banner={banner} />

      {/* Messages Area */}
      <ScrollArea ref={scrollRef} className="flex-1 px-4 py-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-8">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">{getGreeting(userName)}</h3>
            <p className="text-sm text-muted-foreground text-center max-w-xs">
              Find answers quickly for all sectors of real estate, with information from REINZ and
              10+ trusted sources. More sources to be added soon…
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
                <div
                  className={`max-w-[85%] ${
                    message.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'
                  }`}
                >
                  <div className="text-sm prose prose-sm max-w-none">
                    <ReactMarkdown
                      components={{
                        a: ({ href, children }) => (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline font-medium hover:text-primary/80"
                          >
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                )}
              </div>
            ))}

            {isTyping && (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                  <Bot className="w-4 h-4 text-primary-foreground" />
                </div>
                <div className="chat-bubble-ai">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-pulse" />
                    <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-pulse delay-100" />
                    <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-pulse delay-200" />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Input Area */}
      <div className="p-4 border-t border-border bg-card">
        {messages.length > 0 && (
          <div className="flex justify-end mb-2">
            <Button
              onClick={handleClearChat}
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Clear chat
            </Button>
          </div>
        )}
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder={
              banner?.isActive && banner.bannerType === 'warning'
                ? 'AskREINZ is currently unavailable…'
                : 'Ask about NZ real estate...'
            }
            disabled={banner?.isActive === true && banner.bannerType === 'warning'}
            className="flex-1"
          />
          <Button
            onClick={handleSend}
            disabled={
              !input.trim() ||
              isTyping ||
              (banner?.isActive === true && banner.bannerType === 'warning')
            }
            size="icon"
            className="shrink-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-3xs text-muted-foreground text-center mt-2">
          This is general guidance only, not legal advice.
        </p>
      </div>
    </div>
  )
}
